import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../api/firebase/references";
import DynamicSingleSideTab from "../../components/dynamicSingleSideTab/dynamicSingleSideTab";
import { usePayerNameContextFromSingleOrderLayout } from "../../context/payerNameContextFromSingleOrderLayout";
import { useUserThemeContext } from "../../context/userThemeContext";
import { HealthCareFacilityModel } from "../../data-model/healthFacility";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import { LocalStorageKeys } from "../../enums/localStorageKeys";
import { fetchCountsByMasterAuthStatusByAlgolia } from "../../redux/slice/medicalPa/algoliaFacetsCountSlice";
import { fetchMultiFacetsCountFromAlgolia } from "../../redux/slice/medicalPa/algoliaMultiFacetsCountSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import ChevronDown from "../../svg/chevron-down";
import { getItemFromLocalStorage } from "../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { swapUnderscoreHyphen } from "../../utils/swapUnderscoreWithHypen";
import { apiWorkerManager } from "../../workerManagers/workerManager";

const Worklists = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showSideNav,
    setShowSideNav,
    showWorklistNavigation,
    setShowWorklistNavigation,
  } = useUserThemeContext();
  const { setPayerName } = usePayerNameContextFromSingleOrderLayout();

  const [sideLinks, setSideLinks] = useState<{ id: string; label: string }[]>(
    [],
  );
  const [activeTab, setActiveTab] = useState<string>("");
  const [healthFacilityName, setHealthFacilityName] = useState<string>("");
  const [healthFacilityLoading, setHealthFacilityLoading] =
    useState<boolean>(false);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { data: providerInfo } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const { data: algoliaFacetsCount, loading: algoliaFacetsCountLoading } =
    useSelector((state: RootState) => state.algoliaFacetsCount);

  const dispatch = useDispatch<AppDispatch>();

  const authStatusToExclude = ["hold", "query", "auth-required", "pending"];
  const sideNavElementsIdWhereCountIsRequired = [
    "my-pa-orders",
    "auth-required",
    "pending",
    "query",
    "hold",
  ];

  const sideNavElements = [
    { id: "medical-pa-orders", label: "All PA Orders" },
    { id: "my-pa-orders", label: "My PA Orders", shouldShowCount: true },
    { id: "auth-required", label: "Auth Required", shouldShowCount: true },
    { id: "pending", label: "Pending", shouldShowCount: true },
    { id: "query", label: "Query", shouldShowCount: true },
    { id: "hold", label: "Hold", shouldShowCount: true },
  ];

  const mapOfSideNavElementsIdAndCountOfOrders = useMemo(() => {
    return {
      "my-pa-orders":
        algoliaFacetsCount?.facet_results?.["master_auth_status"]?.["new"] ?? 0,
      "auth-required":
        algoliaFacetsCount?.facet_results?.["master_auth_status"]?.[
          "auth_required"
        ] ?? 0,
      pending:
        algoliaFacetsCount?.facet_results?.["master_auth_status"]?.[
          "pending"
        ] ?? 0,
      query:
        algoliaFacetsCount?.facet_results?.["master_auth_status"]?.["query"] ??
        0,
      hold:
        algoliaFacetsCount?.facet_results?.["master_auth_status"]?.["hold"] ??
        0,
    };
  }, [algoliaFacetsCount]);

  useEffect(() => {
    const dynamicAuthOptions =
      authStatusOptions?.map((option) => ({
        id: swapUnderscoreHyphen(option.id),
        label: option.text,
      })) || [];

    const combinedSideLinks = [
      ...sideNavElements,
      ...dynamicAuthOptions.filter(
        (option) => !authStatusToExclude.includes(option.id),
      ),
      { id: "final-worklist", label: "Final Worklist" },
      { id: "final-worklist-upload", label: "Final Worklist Upload" },
    ];

    setSideLinks(combinedSideLinks);

    if (combinedSideLinks.length > 0 && !activeTab) {
      setActiveTab(combinedSideLinks[0].id);
    }
  }, [authStatusOptions]);

  useEffect(() => {
    const shouldShowSideNav = sideLinks.some((element) =>
      location.pathname.startsWith(`/worklists/${element.id}`),
    );

    setShowSideNav(shouldShowSideNav);
  }, [location, sideLinks]);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    apiWorkerManager.enqueueAndProcess({
      patient_id: "",
      org_id: getOrgIdForFetchExternalWorklist(),
      order_id: "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.SIDE_NAV_CLICKED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.WORKLIST,
        source: swapUnderscoreHyphen(tabId),
      },
    });
    navigate(`/worklists/${tabId}`);
  };

  useEffect(() => {
    const parentTable = location.pathname.split("/").pop();
    if (
      parentTable &&
      sideLinks.length > 0 &&
      sideLinks.some((link) => link.id === parentTable)
    ) {
      setActiveTab(parentTable);
      apiWorkerManager.enqueueAndProcess({
        patient_id: "",
        org_id: getOrgIdForFetchExternalWorklist(),
        order_id: "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name: EventsName.WORKLIST_SCREEN_SHOWN,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.WORKLIST,
          selected_tab: swapUnderscoreHyphen(parentTable),
          tab_name: swapUnderscoreHyphen(parentTable),
        },
      });
    } else {
      const parentTableFromLocalStorgae = getItemFromLocalStorage(
        LocalStorageKeys.PARENT_TABLE,
      );
      setActiveTab(
        parentTableFromLocalStorgae
          ? parentTableFromLocalStorgae
          : "medical-pa-orders",
      );
    }
  }, [location.pathname, sideLinks, user?.id, user?.email]);

  useEffect(() => {
    if (providerInfo) {
      dispatch(
        fetchCountsByMasterAuthStatusByAlgolia({
          org_id: getOrgIdForFetchExternalWorklist(),
          facet_attribute: "master_auth_status",
          assigned_to: providerInfo?.DocID ?? "",
        }),
      );
      dispatch(
        fetchMultiFacetsCountFromAlgolia({
          org_id: getOrgIdForFetchExternalWorklist(),
        }),
      );
    }
  }, [providerInfo, showSideNav]);

  useEffect(() => {
    getHealthFacilityName();
    setPayerName("");
  }, []);

  const generateClassNameForOutlet = useMemo(() => {
    if (!showSideNav) {
      return "worklists--outlet px-3 pt-6 worklists--outlet__only";
    } else if (showSideNav && !showWorklistNavigation) {
      return "worklists--outlet px-3 pt-6 worklists--outlet__hide-left";
    }
    return "worklists--outlet px-3 pt-6";
  }, [showSideNav, showWorklistNavigation]);

  const getHealthFacilityName = async () => {
    setHealthFacilityLoading(true);
    const healthFacilityData =
      await FirestoreService.getDocument<HealthCareFacilityModel>(
        FirestoreDocumentReference.healthFacility(
          getItemFromLocalStorage(LocalStorageKeys.HEALTHCARE_FACILITY_ID),
        ),
      );
    if (healthFacilityData?.Name) {
      setHealthFacilityName(healthFacilityData.Name);
    }
    setHealthFacilityLoading(false);
    return healthFacilityData;
  };

  return (
    <div className="worklists--layout relative">
      {showSideNav && (
        <div
          className={`flex h-full ${
            showWorklistNavigation
              ? "worklists--navigation"
              : "worklists--navigation__hide"
          }`}
        >
          {/* Sidebar */}
          <div
            className={`worklist--navigation__container flex flex-col overflow-hidden ${
              showWorklistNavigation
                ? "animate-slide-in w-full"
                : "animate-slide-out w-0"
            }`}
          >
            {/* Facility Header */}
            {healthFacilityLoading ? (
              <div>
                <SpinningLoader />
              </div>
            ) : (
              <div className="worklists--navigation__header-container mr-2 mt-2 w-fit max-w-full truncate rounded-2xl bg-primaryGray-16 px-2 py-1 text-tiny font-semibold shadow-md">
                {healthFacilityName}
              </div>
            )}

            {/* Title */}
            <div
              className={`worklists--navigation__header my-3 w-full truncate pl-2 text-lg font-semibold ${
                showWorklistNavigation
                  ? "animate-fade-up"
                  : "animate-fade-up-out"
              }`}
            >
              Worklists
            </div>

            {/* Links */}
            <div
              className={`worklists--navigation__body flex flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden ${
                showWorklistNavigation
                  ? "animate-fade-down"
                  : "animate-fade-down-out"
              }`}
            >
              {sideLinks.map((sideNavElement, index) => (
                <div
                  key={sideNavElement.id}
                  className={`${
                    showWorklistNavigation
                      ? "animate-stagger-in"
                      : "animate-stagger-out"
                  }`}
                  style={{ transitionDelay: `${10 + index * 50}ms` }}
                >
                  <DynamicSingleSideTab
                    id={sideNavElement.id}
                    label={sideNavElement.label}
                    isActive={activeTab === sideNavElement.id}
                    onTabClick={handleTabClick}
                    shouldShowCount={sideNavElementsIdWhereCountIsRequired.includes(
                      sideNavElement.id,
                    )}
                    count={
                      mapOfSideNavElementsIdAndCountOfOrders[sideNavElement.id]
                    }
                    isLoading={algoliaFacetsCountLoading}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Toggle Icon */}
          <div className="worklist--navigation__icon relative border border-primaryGray-15">
            <div
              className="left-arrow--icon absolute items-center justify-center rounded-full border bg-white p-1 transition-transform duration-300 ease-in-out hover:scale-105 hover:cursor-pointer"
              style={{
                filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25))",
                left: "-14px",
                top: "22px",
              }}
              onClick={() => setShowWorklistNavigation(!showWorklistNavigation)}
            >
              <ChevronDown rotate={!showWorklistNavigation ? 270 : 90} />
            </div>
          </div>
        </div>
      )}

      {/* Main Outlet */}
      <div className={generateClassNameForOutlet}>
        <Outlet />
      </div>
    </div>
  );
};

export default Worklists;
